import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Mail, AlertCircle, CheckCircle, Lock, Eye, EyeOff } from 'lucide-react'
import api from '../services/api'

const ForgotPasswordPage = () => {
  const navigate = useNavigate()
  // 'request' = vraag code aan, 'reset' = code + nieuw wachtwoord, 'done' = klaar
  const [step, setStep] = useState('request')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')

  const handleRequestCode = async (e) => {
    e.preventDefault()
    setError('')
    setInfo('')

    if (!email) {
      setError('E-mailadres is verplicht')
      return
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Ongeldig e-mailadres')
      return
    }

    setLoading(true)

    try {
      await api.post('/auth/forgot-password', { email })
    } catch (err) {
      // Bewust genegeerd: de backend antwoordt altijd hetzelfde, zodat niet
      // te achterhalen is welke e-mailadressen een account hebben.
    } finally {
      setLoading(false)
      setStep('reset')
    }
  }

  const handleResetPassword = async (e) => {
    e.preventDefault()
    setError('')
    setInfo('')

    if (!/^\d{6}$/.test(code)) {
      setError('Vul de 6-cijferige code uit de e-mail in')
      return
    }

    if (password.length < 6) {
      setError('Je nieuwe wachtwoord moet minimaal 6 tekens bevatten')
      return
    }

    if (password !== confirmPassword) {
      setError('De wachtwoorden komen niet overeen')
      return
    }

    setLoading(true)

    try {
      await api.post('/auth/reset-password', { email, code, password })
      setStep('done')
      setTimeout(() => navigate('/login'), 3000)
    } catch (err) {
      const data = err.response?.data
      setError(data?.message || data?.messages?.[0] || 'Wachtwoord resetten mislukt. Probeer het opnieuw.')
    } finally {
      setLoading(false)
    }
  }

  const handleResendCode = async () => {
    setError('')
    setInfo('')
    setLoading(true)

    try {
      await api.post('/auth/forgot-password', { email })
    } catch (err) {
      // Zie handleRequestCode
    } finally {
      setLoading(false)
      setInfo('We hebben een nieuwe code verstuurd.')
    }
  }

  const headerText = {
    request: 'Vul je e-mailadres in en we sturen je een code om je wachtwoord te resetten.',
    reset: `We hebben een code gestuurd naar ${email}. Vul die hieronder in samen met je nieuwe wachtwoord.`,
    done: 'Je wachtwoord is gewijzigd.'
  }[step]

  const inputClass = 'w-full px-4 py-3 pl-12 bg-[#F8FAFC] border border-gray-200 rounded-xl placeholder-[#475569]/50 text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#3EB489] focus:border-[#3EB489] transition'

  return (
    <div className="font-['Inter'] min-h-screen flex flex-col bg-white text-[#0F172A] overflow-x-hidden">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -left-1/2 w-[150%] h-[150%] bg-gradient-to-br from-[#D1FAE5]/50 via-transparent to-transparent rounded-full blur-3xl"></div>
        <div className="absolute -bottom-1/2 -right-1/2 w-[150%] h-[150%] bg-gradient-to-tl from-[#EDE9FE]/50 via-transparent to-transparent rounded-full blur-3xl"></div>
      </div>

      <div className="relative min-h-screen flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <Link to="/" className="inline-flex items-center justify-center space-x-2 mb-8">
              <div className="text-3xl font-black tracking-tighter text-[#0F172A]">
                UGC<span className="text-[#3EB489]">4you</span>
              </div>
            </Link>

            <h2 className="text-3xl sm:text-4xl font-black mb-2">
              {step === 'done' ? 'Gelukt!' : 'Wachtwoord vergeten?'}
            </h2>
            <p className="text-[#475569]">
              {headerText}
            </p>
          </div>

          <div className="relative">
            <div className="relative p-6 sm:p-8 rounded-2xl bg-white border border-gray-200 shadow-lg">
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <div className="flex items-center">
                    <AlertCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" />
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                </div>
              )}

              {info && (
                <div className="mb-6 p-4 bg-white border border-[#3EB489] rounded-xl">
                  <div className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-[#3EB489] mr-3 flex-shrink-0" />
                    <p className="text-sm text-[#475569]">{info}</p>
                  </div>
                </div>
              )}

              {step === 'request' && (
                <form className="space-y-6" onSubmit={handleRequestCode}>
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-[#475569] mb-2">
                      E-mailadres
                    </label>
                    <div className="relative">
                      <input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); setError(''); }}
                        className={inputClass}
                        placeholder="jouw.email@voorbeeld.nl"
                      />
                      <Mail className="w-5 h-5 text-[#475569]/50 absolute left-4 top-3.5" />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-bold rounded-xl text-white bg-[#3EB489] hover:bg-[#359E78] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3EB489] disabled:opacity-50 disabled:cursor-not-allowed transition transform hover:scale-105 shadow-lg"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      'Verstuur resetcode'
                    )}
                  </button>
                </form>
              )}

              {step === 'reset' && (
                <form className="space-y-6" onSubmit={handleResetPassword}>
                  <div>
                    <label htmlFor="code" className="block text-sm font-semibold text-[#475569] mb-2">
                      Resetcode uit de e-mail
                    </label>
                    <input
                      id="code"
                      name="code"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      value={code}
                      onChange={(e) => { setCode(e.target.value.replace(/\D/g, '')); setError(''); }}
                      className="w-full px-4 py-3 bg-[#F8FAFC] border border-gray-200 rounded-xl text-center text-2xl font-black tracking-[0.4em] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#3EB489] focus:border-[#3EB489] transition"
                      placeholder="000000"
                    />
                    <p className="mt-2 text-xs text-[#475569]">
                      De code is 30 minuten geldig.
                    </p>
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-semibold text-[#475569] mb-2">
                      Nieuw wachtwoord
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        value={password}
                        onChange={(e) => { setPassword(e.target.value); setError(''); }}
                        className={inputClass}
                        placeholder="Minimaal 6 tekens"
                      />
                      <Lock className="w-5 h-5 text-[#475569]/50 absolute left-4 top-3.5" />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-3.5 text-[#475569]/50 hover:text-[#475569] transition"
                        aria-label={showPassword ? 'Verberg wachtwoord' : 'Toon wachtwoord'}
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-semibold text-[#475569] mb-2">
                      Herhaal nieuw wachtwoord
                    </label>
                    <div className="relative">
                      <input
                        id="confirmPassword"
                        name="confirmPassword"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                        className={inputClass}
                        placeholder="Herhaal je wachtwoord"
                      />
                      <Lock className="w-5 h-5 text-[#475569]/50 absolute left-4 top-3.5" />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-bold rounded-xl text-white bg-[#3EB489] hover:bg-[#359E78] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3EB489] disabled:opacity-50 disabled:cursor-not-allowed transition transform hover:scale-105 shadow-lg"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      'Wachtwoord opslaan'
                    )}
                  </button>

                  <div className="text-center space-y-2">
                    <button
                      type="button"
                      onClick={handleResendCode}
                      disabled={loading}
                      className="text-sm text-[#3EB489] hover:text-[#1670c9] font-semibold transition disabled:opacity-50"
                    >
                      Geen code ontvangen? Stuur opnieuw
                    </button>
                    <div>
                      <button
                        type="button"
                        onClick={() => { setStep('request'); setCode(''); setError(''); setInfo(''); }}
                        className="text-sm text-[#475569] hover:text-[#0F172A] transition"
                      >
                        Ander e-mailadres gebruiken
                      </button>
                    </div>
                  </div>
                </form>
              )}

              {step === 'done' && (
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 bg-white border-2 border-[#3EB489] rounded-full flex items-center justify-center mx-auto">
                    <CheckCircle className="w-8 h-8 text-[#3EB489]" />
                  </div>
                  <p className="text-sm text-[#475569]">
                    Je wachtwoord is gewijzigd. Je wordt doorgestuurd naar de inlogpagina.
                  </p>
                  <Link
                    to="/login"
                    className="inline-flex justify-center py-3 px-6 text-sm font-bold rounded-xl text-white bg-[#3EB489] hover:bg-[#359E78] transition shadow-lg"
                  >
                    Direct inloggen
                  </Link>
                </div>
              )}
            </div>
          </div>

          <div className="text-center">
            <Link
              to="/login"
              className="inline-flex items-center text-[#475569] hover:text-[#0F172A] transition font-medium"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Terug naar inloggen
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ForgotPasswordPage
